import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { format, parseISO } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';

import residentCardsService from '../../service/api/ResidentCardsService';
import ResidentCardDialog from './ResidentCardDialog';
import { ResidentCard } from '../../types/ResidentCard';
import { getFileFromBuffer } from '../_shared/getFile';
import getPaginatorTemplate from '../_shared/getPaginatorTemplate';
import { usePagination } from '../_shared/hooks/usePagination';
import { useDebounce } from '../_shared/hooks/useDebounce';
import { ColumnDefinitions, useColumnFactory } from '../_shared/columnFactory';
import { generateFiltersFromColumns } from '../_shared/tableConfiguration';
import { SearchMode, TableFilterData } from '../../types/general-types';
import { FULL_DATE_FORMAT } from '../../settings';

export const ResidentCardManagement = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [residentCards, setResidentCards] = useState<ResidentCard[]>(null);

  const [residentCardToDelete, setResidentCardToDelete] = useState<ResidentCard>();
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<ResidentCard>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);

  const columnDefinitions: ColumnDefinitions = useMemo(
    () => ({
      number: {
        type: 'string',
        translationKey: 'residentCards.number',
      },
      propertyName: {
        type: 'string',
        translationKey: 'residentCards.property',
      },
      downloadedAt: {
        type: 'date',
        translationKey: 'residentCards.downloaded_at',
        dateFormat: FULL_DATE_FORMAT,
        bodyTemplate: (data: ResidentCard) =>
          data.downloadedAt ? <>{format(parseISO(data.downloadedAt), FULL_DATE_FORMAT)}</> : <>-</>,
      },
    }),
    []
  );

  const DEFAULT_FILTERS = useMemo(() => ({ ...generateFiltersFromColumns(columnDefinitions) }), [columnDefinitions]);

  const {
    paginatorState,
    setPaginatorState,
    paginationParameters,
    setPaginationParameters,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination({ rows: 10, showRowsPerPageDropdown: false, orderColumn: 'number', ascending: true });

  const [filterState, setFilterState] = useState<TableFilterData>(structuredClone(DEFAULT_FILTERS));
  const [filterRequest, setFilterRequest] = useState<TableFilterData>(structuredClone(DEFAULT_FILTERS));

  const calculateFilterRequest = (data: TableFilterData, globalFilterValue: string) => {
    const filters = structuredClone(data);
    if (globalFilterValue) {
      filters['global'] = {
        value: globalFilterValue,
        searchMode: SearchMode.CONTAINS,
      } as any;
    }
    setFilterRequest(filters);
  };

  const debouncedCalculateFilterRequest = useDebounce(calculateFilterRequest, 400);

  const clearFilter = () => {
    setFilterState(structuredClone(DEFAULT_FILTERS));
    setFilterRequest(structuredClone(DEFAULT_FILTERS));
    setGlobalFilter('');
  };

  const loadData = async () => {
    setLoading(true);
    const response = await residentCardsService.search(filterRequest, paginationParameters);

    const totalCount = response.totalCount;
    const totalPages = totalCount < 1 ? 1 : Math.ceil(totalCount / paginationParameters.page.size);
    setPaginatorState((prevState) => ({
      ...prevState,
      totalPages,
      currentPage: prevState.currentPage > totalPages ? 1 : prevState.currentPage,
    }));

    setResidentCards(response.data);
    setLoading(false);
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
    // filter changes reset the page to 1, which triggers this effect anyway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationParameters]);

  useEffect(() => {
    handlePageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRequest]);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setGlobalFilter(value);
    debouncedCalculateFilterRequest(filterState, value);
  };

  const handleChangeFilter = (name: string, value: any, triggerDataRefresh = false) => {
    const newFilters = { ...filterState };
    if (Array.isArray(value) && value.filter((v) => v != null).length === 0) {
      value = [];
    }
    newFilters[name].value = value;
    setFilterState(newFilters);

    if (triggerDataRefresh) {
      calculateFilterRequest(newFilters, globalFilter);
    }
  };

  const handleFilterApplyClick = () => {
    calculateFilterRequest(filterState, globalFilter);
  };

  const handleSort = (e: any) => {
    setPaginationParameters({
      ...paginationParameters,
      order: {
        isAscending: e.sortOrder === 1,
        orderColumn: e.sortField,
      },
    });
  };

  const { createColumns } = useColumnFactory(filterState, handleChangeFilter, handleFilterApplyClick, t);

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
  };

  const confirmDeleteResidentCard = (residentCard: ResidentCard) => {
    setResidentCardToDelete(residentCard);
    setDeleteDialogVisible(true);
  };

  const deleteResidentCard = async () => {
    setLoading(true);
    await residentCardsService.delete(residentCardToDelete.id);
    setDeleteDialogVisible(false);
    await loadData();
  };

  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const editResidentCard = (residentCard: ResidentCard) => {
    setSelectedRowToEdit(residentCard);
    setAddEditDialogVisible(true);
  };

  const addResidentCard = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogVisible(true);
  };

  const print = async (residentCard: ResidentCard) => {
    const response = await residentCardsService.print(residentCard.id);
    // file name intentionally left blank to open pdf in new tab
    getFileFromBuffer(response, 'application/pdf', null);
  };

  const exportToExcel = async () => {
    // Export uses the currently applied filters and sort (all matching rows).
    const response = await residentCardsService.exportToExcel(filterRequest, paginationParameters);
    getFileFromBuffer(
      response,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'karty-mieszkancow.xlsx'
    );
  };

  const residentCardsTableHeader = (
    <div className="table-header">
      {t('residentCards.header')}
      <span>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          className="p-button-outlined mr-2"
          label={t('common.clear')}
          onClick={clearFilter}
        />
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={globalFilter}
            onChange={onGlobalFilterChange}
            placeholder={t('common.search')}
          />
        </span>
        <Button
          type="button"
          icon="pi pi-file-excel"
          label={t('common.export')}
          className="p-button-outlined ml-2"
          onClick={exportToExcel}
        />
        <Button
          type="button"
          icon="pi pi-plus"
          label={t('common.add')}
          className="p-button ml-2"
          onClick={addResidentCard}
        />
      </span>
    </div>
  );

  const actionTemplate = (data: ResidentCard) => (
    <span>
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => editResidentCard(data)}
      ></Button>
      <Button
        type="button"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => confirmDeleteResidentCard(data)}
      ></Button>
      <Button
        type="button"
        icon="pi pi-print"
        tooltip={t('common.print')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success"
        onClick={async () => {
          await print(data);
        }}
      ></Button>
    </span>
  );

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={hideDeleteDialog} />
      <Button
        label={t('common.yes')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={async () => {
          await deleteResidentCard();
        }}
      />
    </>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={residentCards}
            lazy
            paginator
            className="p-datatable-customers"
            rows={paginatorState.rows}
            dataKey="id"
            sortOrder={paginationParameters.order.isAscending ? 1 : -1}
            sortField={paginationParameters.order.orderColumn}
            onSort={handleSort}
            rowHover
            emptyMessage={t('residentCards.empty_message')}
            loading={loading}
            header={residentCardsTableHeader}
            filterDisplay="menu"
            filters={filterState}
            paginatorTemplate={getPaginatorTemplate(paginatorState, handlePageChange, handleRowsPerPageChange)}
          >
            {createColumns(columnDefinitions)}
            <Column
              headerStyle={{ width: '12rem' }}
              style={{ minWidth: '12rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>

          <ResidentCardDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            residentCard={selectedRowToEdit}
          />

          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {residentCardToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: residentCardToDelete.number }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
